import MainLayout from '../components/MainLayout';
import { useContext, useEffect } from 'react';
import AddFeed, { useAddFeedPath } from '../components/AddFeed';
import { CategoryCrumbs, TitleCrumbs, addFeedListTitle } from '../components/AddFeedBrowse';
import { ShortcutsContext, ShortcutGroup, GO_HOME_SHORTCUT } from '../components/ShortcutsOverlay';
import { RouterContext, goTo } from 't-lib-ui/router';
import PageHeader from '../components/PageHeader';
import HeaderAuthButtons from '../components/HeaderAuthButtons';
import './ScrollPage.css';
import { useFlowFormGlobal, FlowFormLeaveContext, registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import { isAddFeedFromHome, isTopicViaAllTopics, requestHomeScrollRestore } from './homeScroll';
import OfflineNotice from '../components/OfflineNotice';
import { useReadingOffline } from '../utils/useOfflineMode';

const ADD_FEED_SHORTCUTS: ShortcutGroup[] = [{ title: 'Navigation', shortcuts: [GO_HOME_SHORTCUT] }];

const AddFeedPage: React.FC = () => {
  const readingOffline = useReadingOffline();
  const handleFeedAdded = (_feedId: string) => {
    // Stay on the page - user can click "View" button to navigate to the feed
  };

  useFlowFormGlobal('.flowForm.mainForm');
  const routerCtx = useContext(RouterContext);
  const { category, isTopicsView, listId } = useAddFeedPath();
  const fromHome = isAddFeedFromHome();
  const navigate = (path: string) => {
    // Back to Home where the user left it (at the Categories section).
    if (path == '/a/home') {
      requestHomeScrollRestore();
      return goTo(routerCtx, path);
    }
    goTo(routerCtx, path);
    document.querySelector('.scrollPage')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    registerKeyHandler('add-feed-page', {
      'h': () => {
        const ae = document.activeElement as HTMLElement | null;
        if (ae?.tagName == 'INPUT' || ae?.tagName == 'TEXTAREA' || ae?.isContentEditable) return false;
        goTo(routerCtx, '/a/home');
        return true;
      },
    });
    return () => unregisterKeyHandler('add-feed-page');
  }, [routerCtx]);

  // Sub pages show where they are in the header, with links back (see CategoryCrumbs).
  const crumbs = category ? <CategoryCrumbs category={category} fromHome={fromHome} viaAllTopics={isTopicViaAllTopics()} onNavigate={navigate} />
    : isTopicsView ? <TitleCrumbs title="All topics" fromHome={fromHome} onNavigate={navigate} />
    : listId ? <TitleCrumbs title={addFeedListTitle(listId) ?? 'Feeds'} onNavigate={navigate} />
    : undefined;

  return (
    <ShortcutsContext.Provider value={ADD_FEED_SHORTCUTS}>
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
    <MainLayout
      selectedFeed="add"
      right={({ onExpand }) => (
        <div className="scrollPage">
          <PageHeader title={crumbs ?? 'Add Feed'} onMenu={onExpand} rightContent={<HeaderAuthButtons hideOnMobile={!!crumbs} />} />

          {readingOffline
            ? <OfflineNotice>Feeds can't be found or added until you're back online.</OfflineNotice>
            : <AddFeed onFeedAdded={handleFeedAdded} autoFocus />}
        </div>
      )}
      startWithLeftCollapsed={typeof window !== 'undefined' && window.innerWidth <= 750}
    />
    </FlowFormLeaveContext.Provider>
    </ShortcutsContext.Provider>
  );
};

export default AddFeedPage;
